/**
 * SettingsRoute (Plan 01-13, Task 3).
 *
 * Authenticated shell for the admin + per-member settings surfaces.
 * Phase 1 renders a minimal header — Phase 6 polishes with section
 * cards, force-reload button, admin re-auth flow, etc.
 *
 * Auth check: GET /api/auth/me on mount (Plan 03 B1 contract).  The
 * backend returns 200 with `{ member, session }` when the
 * `stell_sid` HttpOnly cookie is valid, or 401 otherwise.  On 401 we
 * hard-redirect to /login — the cookie is HttpOnly so we can't sniff
 * it from document.cookie; the round-trip is authoritative.
 *
 * Note: DashboardRoute (/) is explicitly authless per DASH-06; this
 * guard wraps SettingsRoute ONLY.
 *
 * #159 (steg 9d, Stell_Meg): «Meg» med tilbakeknapp, profilkortet, temaet
 * for enheten, veggmodus, kortet med Bytt passord og PIN, lenkene for
 * voksne og Send tilbakemelding, og «Logg ut» nederst. Skallet tegner ikke
 * sin egen topp her (`drawsOwnHeader`). Varsler-bryteren i skissen er ny
 * oppførsel 7 og er utelatt, og det samme er «Endre» på profilkortet (det
 * finnes ingen profilredigering for en selv).
 */
import { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ThemeToggle } from './ThemeToggle';
import { WallModeToggle } from './WallModeToggle';
import { AccountSection, LogoutButton, ProfileCard } from './AccountSection';
import { useSession } from '../auth/useSession';
import { FeedbackWidget } from '../feedback/FeedbackWidget';
import { ADMIN_LIST, ADMIN_ROW } from '../admin/adminStyles';
import { CONSOLE_MAIN } from '../parents/ParentConsoleRoute';
import { ScreenHeader } from '../../components/ScreenHeader';
import { Icon } from '../../components/primitives';

type SessionState = 'loading' | 'authed' | 'unauthed';

function useHasSession(): SessionState {
  const [state, setState] = useState<SessionState>('loading');
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch('/api/auth/me', {
          credentials: 'same-origin',
        });
        if (cancelled) return;
        setState(res.ok ? 'authed' : 'unauthed');
      } catch {
        if (!cancelled) setState('unauthed');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}

export function SettingsRoute() {
  const { t } = useTranslation();
  const state = useHasSession();
  const { data: session, isPending } = useSession();
  const isAdmin = session?.member?.role === 'admin';
  // Skallet tegner ingen topp på Meg, så toppen står også mens vi venter.
  const header = (
    <ScreenHeader title={t('settings.me.title')} back={{ to: '/', history: true }} hideAvatar />
  );

  // Venter også på medlemmet, så profilkortet og lenkene ikke tegnes tomme
  // og så dukker opp.
  if (state === 'loading' || (state === 'authed' && isPending)) {
    return (
      <main className={CONSOLE_MAIN}>
        {header}
        <p data-testid="settings-loading" className="m-0 text-sm text-ink-2">
          {t('login.loading')}
        </p>
      </main>
    );
  }
  if (state === 'unauthed') {
    return <Navigate to="/login" replace />;
  }

  return (
    <main data-testid="settings-route" className={CONSOLE_MAIN}>
      {header}
      <ProfileCard />
      <ThemeToggle />
      <WallModeToggle />
      <AccountSection />
      {session?.member ? (
        <div className={`${ADMIN_LIST} divide-y divide-card-line`}>
          {isAdmin ? (
            <Link to="/foreldre" data-testid="settings-console-link" className={ADMIN_ROW}>
              <span className="min-w-0 flex-1">{t('settings.admin.console_label')}</span>
              <Icon.ChevR size={18} className="shrink-0 text-ink-2" />
            </Link>
          ) : null}
          {isAdmin ? (
            <Link to="/settings/admin" data-testid="settings-admin-link" className={ADMIN_ROW}>
              <span className="min-w-0 flex-1">{t('settings.admin.link_label')}</span>
              <Icon.ChevR size={18} className="shrink-0 text-ink-2" />
            </Link>
          ) : null}
          {/* AI-21 (#193): alle roller ser hva assistenten husker om dem. */}
          <Link to="/settings/hukommelse" data-testid="settings-memory-link" className={ADMIN_ROW}>
            <span className="min-w-0 flex-1">{t('assistant.memory.settings.title')}</span>
            <Icon.ChevR size={18} className="shrink-0 text-ink-2" />
          </Link>
          {/* D11 (#153): tilbakemelding åpnes herfra, ikke fra en global knapp. */}
          <FeedbackWidget />
        </div>
      ) : null}
      <LogoutButton variant="link" />
    </main>
  );
}
